import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { filter, finalize } from 'rxjs';
import { ConfirmComponent, ConfirmData } from 'src/app/components/dialogs/confirm-dialog/confirm.component';
import { OrderInquiryThreadComponent } from 'src/app/components/order-inquiry-thread/order-inquiry-thread.component';
import { BackendService } from 'src/app/services/backend.service';
import { NotificationService } from 'src/app/services/notification.service';
import { components } from 'src/app/shared/types/api.type';

type OrderInquiry = components['schemas']['OrderInquiryDto'];
type Decision = NonNullable<components['schemas']['AnswerOrderInquiryDto']['decision']>;

export const ORDER_INQUIRY_NOTIFICATION_TYPE_KEYS = ['equipment_order_inquiry', 'equipment_return_request', 'equipment_order_inquiry_answer'];

/**
 * A question about a PSA order or a return request (SHIP-1938). The supplier or the Verwaltung answers
 * here; the orderer sees the conversation too. Subscribers and the orderer get notifications of their
 * own that point at the request via `metadata.inquiryNotificationId`.
 *
 * Since SHIP-1967 both sides go on writing after the first answer. Only the first answer takes the
 * decision, so the decision buttons come only then.
 *
 * A cancellation request (SHIP-742) is answered by cancelling or rejecting with a reason; a conflict - the
 * supplier confirmed or shipped a cancelled order anyway - by marking it cleared.
 */
@Component({
  selector: 'app-order-inquiry-notification-panel',
  templateUrl: './order-inquiry-notification-panel.component.html',
  styleUrls: ['./order-inquiry-notification-panel.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    TranslocoModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    OrderInquiryThreadComponent
  ]
})
export class OrderInquiryNotificationPanelComponent implements OnInit {
  @Input({ required: true }) notification!: components['schemas']['InAppNotification'];
  @Output() readonly answered = new EventEmitter<void>();

  inquiry?: OrderInquiry;
  loadFailed = false;
  answer = '';
  reply = '';
  submitting = false;

  constructor(
    private readonly backendService: BackendService,
    private readonly notify: NotificationService,
    private readonly translate: TranslocoService,
    private readonly dialog: MatDialog
  ) {}

  private get inquiryId(): number {
    const metadata = (this.notification.metadata ?? {}) as { inquiryNotificationId?: number };
    return metadata.inquiryNotificationId ?? this.notification.id;
  }

  ngOnInit(): void {
    this.backendService
      .get<'orderInquiry/get'>('api/v2/equipment/order-inquiries/{id}', { path: { id: this.inquiryId } })
      .subscribe({
        next: (inquiry) => (this.inquiry = inquiry),
        error: () => (this.loadFailed = true)
      });
  }

  get labelKey(): string {
    if (!this.inquiry) return '';
    if (this.inquiry.kind === 'cancellation_conflict') return `notifications.order_inquiry.conflict_${this.inquiry.conflictEvent ?? 'order_confirmation'}`;
    return this.inquiry.kind === 'return_request'
      ? 'views.psa.inquiry_return_request'
      : `views.psa.inquiry_category_${this.inquiry.category}`;
  }

  get isCancellationRequest(): boolean {
    return this.inquiry?.category === 'cancel';
  }

  get isConflict(): boolean {
    return this.inquiry?.kind === 'cancellation_conflict';
  }

  /** One message after the question at least: from then on the conversation replaces question and answer */
  get hasConversation(): boolean {
    return (this.inquiry?.messages.length ?? 0) > 1 || !!this.inquiry?.closedAt;
  }

  get questionLabel(): string {
    return this.labelKey ? this.translate.translate(this.labelKey) : '';
  }

  get decisionKey(): string | null {
    const decision = this.inquiry?.decision;
    return decision ? `notifications.order_inquiry.decision_${decision}` : null;
  }

  /** Cancelling cannot be undone: the order is cancelled for good, so ask once more. */
  cancelOrder(): void {
    if (!this.inquiry || this.submitting) return;
    this.dialog
      .open(ConfirmComponent, {
        maxWidth: '480px',
        data: {
          title: this.translate.translate('notifications.order_inquiry.cancel_confirm_title', { orderNumber: this.inquiry.orderNumber }),
          body: this.translate.translate('notifications.order_inquiry.cancel_confirm_body', { supplier: this.inquiry.supplierName })
        } satisfies ConfirmData
      })
      .afterClosed()
      .pipe(filter((confirmed) => !!confirmed))
      .subscribe(() => this.submit('cancelled'));
  }

  submit(decision?: Decision): void {
    const text = this.answer.trim();
    const textRequired = !decision || decision === 'rejected';
    if (!this.inquiry || (textRequired && !text) || this.submitting) return;
    this.submitting = true;
    this.backendService
      .post<'orderInquiry/answer'>('api/v2/equipment/order-inquiries/{id}/answer', {
        path: { id: this.inquiry.id },
        body: { answer: text || undefined, decision }
      })
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (inquiry) => {
          this.inquiry = inquiry;
          this.notify.success(this.translate.translate('notifications.order_inquiry.answer_sent'));
          this.answered.emit();
        },
        error: (err) => this.notify.error(err?.error?.message ?? this.translate.translate('notifications.order_inquiry.answer_failed'))
      });
  }

  /** Ends the conversation for both sides; its notifications are archived, nobody can write any more. */
  closeConversation(): void {
    if (!this.inquiry?.canClose || this.submitting) return;
    this.dialog
      .open(ConfirmComponent, {
        maxWidth: '480px',
        data: {
          title: this.translate.translate('notifications.order_inquiry.close_confirm_title', { orderNumber: this.inquiry.orderNumber }),
          body: this.translate.translate('notifications.order_inquiry.close_confirm_body')
        } satisfies ConfirmData
      })
      .afterClosed()
      .pipe(filter((confirmed) => !!confirmed))
      .subscribe(() => {
        this.submitting = true;
        this.backendService
          .post<'orderInquiry/close'>('api/v2/equipment/order-inquiries/{id}/close', { path: { id: this.inquiry!.id } })
          .pipe(finalize(() => (this.submitting = false)))
          .subscribe({
            next: (inquiry) => {
              this.inquiry = inquiry;
              this.notify.success(this.translate.translate('notifications.order_inquiry.closed'));
              this.answered.emit();
            },
            error: (err) => this.notify.error(err?.error?.message ?? this.translate.translate('notifications.order_inquiry.close_failed'))
          });
      });
  }

  sendReply(): void {
    const text = this.reply.trim();
    if (!this.inquiry || !text || this.submitting) return;
    this.submitting = true;
    this.backendService
      .post<'orderInquiry/addMessage'>('api/v2/equipment/order-inquiries/{id}/messages', { path: { id: this.inquiry.id }, body: { text } })
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (inquiry) => {
          this.inquiry = inquiry;
          this.reply = '';
          this.notify.success(this.translate.translate('notifications.order_inquiry.reply_sent'));
          this.answered.emit();
        },
        error: (err) => this.notify.error(err?.error?.message ?? this.translate.translate('notifications.order_inquiry.reply_failed'))
      });
  }
}
